import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { UserService } from '@/service/user.service';
import { GraphqlService } from '@/service/graphql.service';
import { Category } from '@/model/category';
import { ManagedUser, User } from '@/model/user';
import { Company } from '@/model/company';
import { LoadingIndicationComponent } from '../components/loading-indication/loading-indication.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ManagedUserManagementComponent } from '@/app/components/managed-user-management/managed-user-management.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { KeycloakService } from 'keycloak-angular';
import { forkJoin } from 'rxjs';
@Component({
  selector: 'app-profile',
  imports: [
    MatCardModule,
    FormsModule,
    LoadingIndicationComponent,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    MatProgressBarModule,
    ManagedUserManagementComponent,
    MatExpansionModule,
  ],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
  standalone: true,
})
export class ProfileComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef;
  isUploading = false;
  availableCategories: Category[] = [];
  userInformation: User | undefined;
  managedUsers: ManagedUser[] = [];
  showNewUserForm = false;
  activeExpansionPanel = -1;
  changedUser: ManagedUser | null = null;
  loadingManagedUsers = false;

  userForm: FormGroup;
  companyForm: FormGroup;

  loadingUserProfile = {
    loading: false,
    saving: false,
  };

  loadingCompanyProfile = {
    loading: false,
    saving: false,
  };

  constructor(
    private userService: UserService,
    private graphqlService: GraphqlService,
    private fb: FormBuilder,
    private keycloakService: KeycloakService,
  ) {
    this.userForm = this.fb.group({
      id: ['', [Validators.required]],
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', []],
      gender: ['', [Validators.required]],
      birthYear: [
        '',
        [
          Validators.required,
          Validators.min(1900),
          Validators.max(new Date().getFullYear()),
        ],
      ],
      zipCode: ['', [Validators.required]],
      profilePictureUrl: [''],
      profilePictureId: [''],
      categories: [[], [Validators.required]],
    });

    this.companyForm = this.fb.group({
      companyName: ['', [Validators.required]],
      contactPersonName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      uid: ['', []],
      website: ['', [Validators.required]],
      phone: ['', [Validators.required]],
      categories: [[], [Validators.required]],
      address: this.fb.group({
        street: ['', Validators.required],
        city: ['', Validators.required],
        postalCode: ['', [Validators.required]],
      }),
    });
  }

  ngOnInit(): void {
    this.initializeUserForm();
    this.initializeCompanyForm();
    this.loadManagedUsers();
  }

  resetUserForm() {
    this.userForm.reset();
  }

  resetCompanyForm() {
    this.companyForm.reset();
  }

  initializeUserForm() {
    this.loadingUserProfile.loading = true;
    this.userService.getUserInfo().subscribe((user) => {
      this.userInformation = user;
      if (this.availableCategories.length === 0) {
        this.graphqlService.allCategories().subscribe((categories) => {
          this.availableCategories = categories.filter(
            (category) => !category.parentId,
          );
          this.patchUserFormValues(user);
        });
      } else {
        this.patchUserFormValues(user);
      }
      this.loadingUserProfile.loading = false;
    });
  }

  // Force refresh user data (clear cache and reload)
  refreshUserData() {
    this.loadingUserProfile.loading = true;
    // Clear the cache first
    this.userService.clearUserCache();
    // Then reload the data
    this.userService.refreshUserInfo().subscribe({
      next: (user) => {
        console.log('Fresh user data received:', user);
        this.userInformation = user;
        // Update form with fresh data
        this.patchUserFormValues(user);
        this.loadingUserProfile.loading = false;
      },
      error: (error) => {
        console.error('Error refreshing user data:', error);
        this.loadingUserProfile.loading = false;
      }
    });
  }

  patchUserFormValues(user: User) {
    console.log('🔧 patchUserFormValues called with user:', user);
    console.log('🔧 Current form values before patch:', this.userForm.value);
    
    const newValues = {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      gender: user.gender,
      birthYear: user.birthYear,
      zipCode: user.zipCode,
      profilePictureUrl: user.profilePicture?.url || '',
      profilePictureId: user.profilePicture?.id || '',
      categories: user.categories?.map((cat) => cat.id) || [],
    };
    
    console.log('🔧 Patching form with values:', newValues);
    this.userForm.patchValue(newValues);
    
    console.log('🔧 Form values after patch:', this.userForm.value);
  }

  initializeCompanyForm() {
    this.loadingCompanyProfile.loading = true;
    this.userService.getUserInfo().subscribe((user) => {
      if (user.company && user.company.companyName) {
        const company = user.company as Company;
        if (this.availableCategories.length === 0) {
          this.graphqlService.allCategories().subscribe((categories) => {
            this.availableCategories = categories.filter(
              (category) => !category.parentId,
            );
            this.patchCompanyFormValues(company);
          });
        } else {
          this.patchCompanyFormValues(company);
        }
      }
      this.loadingCompanyProfile.loading = false;
    });
  }

  private patchCompanyFormValues(company: Company) {
    this.companyForm.patchValue({
      companyName: company.companyName,
      contactPersonName: company.contactPersonName,
      email: company.email,
      uid: company.uid,
      website: company.website,
      phone: company.phone,
      categories: company.categoryIds || [],
      address: {
        street: company.address?.streetAndNumber,
        city: company.address?.city,
        postalCode: company.address?.postalCode,
      },
    });
  }

  loadCategories() {
    this.graphqlService.allCategories().subscribe((categories) => {
      this.availableCategories = categories.filter(
        (category) => !category.parentId,
      );
    });
  }

  getCategoryName(categoryId: string): string | undefined {
    return this.availableCategories.find((c) => c.id === categoryId)?.name;
  }

  get categoriesControl() {
    return this.companyForm.get('categories');
  }

  updateUser() {
    console.log('💾 updateUser called');
    console.log('💾 Form values being saved:', this.userForm.value);
    
    this.loadingUserProfile.saving = true;
    const userFormData = {
      ...this.userForm.value,
      profilePicture: this.userForm.value.profilePictureId
        ? {
            id: this.userForm.value.profilePictureId,
            url: this.userForm.value.profilePictureUrl,
          }
        : null,
    };
    delete userFormData.profilePictureId;
    delete userFormData.profilePictureUrl;
    const categoryIds = userFormData.categories;
    delete userFormData.categories;

    console.log('💾 Data being sent to API:', { userFormData, categoryIds });

    // Create an observable that combines both operations
    forkJoin({
      userUpdate: this.userService.updateUserPersonalSettings(userFormData),
      categoriesUpdate: this.userService.updateUserCategories(
        this.userInformation!,
        categoryIds,
      ),
    }).subscribe({
      next: () => {
        console.log('💾 User update successful, refreshing data...');
        this.loadingUserProfile.saving = false;
        // Don't reset form here - let refreshUserData handle the update
        // Use refreshUserData to force fresh data after update
        this.refreshUserData();
      },
      error: (error) => {
        console.error('💾 Error updating user:', error);
        this.loadingUserProfile.saving = false;
      },
    });
  }

  updateCompany() {
    if (this.companyForm.valid && this.userInformation) {
      this.loadingCompanyProfile.saving = true;
      const companyData: Partial<Company> = {
        email: this.companyForm.value.email,
        companyName: this.companyForm.value.companyName,
        contactPersonName: this.companyForm.value.contactPersonName,
        website: this.companyForm.value.website,
        phone: this.companyForm.value.phone,
        uid: this.companyForm.value.uid,
        categoryIds: this.companyForm.value.categories,
        address: {
          streetAndNumber: this.companyForm.value.address.street,
          city: this.companyForm.value.address.city,
          postalCode: this.companyForm.value.address.postalCode,
        },
        acceptedPrivacyPolicy: true,
        acceptedAgb: true,
      };
      this.userService.createOrUpdateCompany(companyData).subscribe({
        next: () => {
          this.loadingCompanyProfile.saving = false;
          // Clear cache and refresh user data (which includes company data)
          this.userService.clearUserCache();
          this.userService.refreshUserInfo().subscribe({
            next: (user) => {
              console.log('Fresh user data after company update:', user);
              this.userInformation = user;
              // Update both user and company forms with fresh data
              this.patchUserFormValues(user);
              if (user.company) {
                this.patchCompanyFormValues(user.company);
              }
            },
            error: (error) => {
              console.error('Error refreshing user data after company update:', error);
            }
          });
        },
        error: (error) => {
          console.error('Error updating company:', error);
          this.loadingCompanyProfile.saving = false;
        },
      });
    }
  }

  isCompanyUser(): boolean {
    return (
      this.userInformation?.company !== null ||
      this.userInformation?.roles?.includes('company') !== undefined
    );
  }

  isAdminUser(): boolean {
    return this.keycloakService.getUserRoles().includes('admin');
  }

  triggerFileInput() {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      if (this.isValidImageFile(file)) {
        this.uploadProfilePicture(file);
      } else {
        // Handle invalid file type
        console.error('Invalid file type. Please select an image file.');
      }
    }
  }

  private isValidImageFile(file: File): boolean {
    const acceptedImageTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
    ];
    return acceptedImageTypes.includes(file.type);
  }

  private uploadProfilePicture(file: File) {
    this.isUploading = true;
    this.userService.uploadProfilePicture(file).subscribe({
      next: (profilePicture) => {
        this.userForm.patchValue({
          profilePictureUrl: profilePicture.url,
          profilePictureId: profilePicture.id,
        });
        this.updateUser();
        this.isUploading = false;
      },
      error: (error) => {
        console.error('Error uploading profile picture:', error);
        this.isUploading = false;
      },
    });
  }

  loadManagedUsers() {
    this.loadingManagedUsers = true;

    this.userService.getManagedUsers().subscribe({
      next: (users) => {
        this.managedUsers = users;
        this.loadingManagedUsers = false;
        // Restore active panel after a short delay to ensure panel is rendered
        setTimeout(() => {
          this.activeExpansionPanel = this.nextActivePanel;
        }, 0);
      },
      error: (error) => {
        console.error('Error loading managed users:', error);
        this.loadingManagedUsers = false;
      },
    });
  }

  addNewManagedUser() {
    this.showNewUserForm = true;
    // Set active panel to the new user form
    this.activeExpansionPanel = this.managedUsers.length;
  }

  get nextActivePanel() {
    if (this.changedUser) {
      return this.managedUsers.findIndex(
        (user) => user.id === this.changedUser?.id,
      );
    }
    return -1;
  }

  onManagedUserChanged(userChanged: ManagedUser | null) {
    this.changedUser = userChanged;
    this.showNewUserForm = false;
    if (userChanged === null) {
      this.managedUsers = [];
    }
    this.loadManagedUsers();
  }

  onPanelOpened(index: number) {
    this.activeExpansionPanel = index;
  }
}
